import { PercentIcon } from '@phosphor-icons/react'
import { ReactNode } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ProcessingFeeType } from '@/api/building'
import { NumericInputFormField, SelectFormField, SwitchFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import {
  FieldBooleanBadge,
  FieldGroup,
  FieldGroupHeader,
  FieldRow,
  FieldsTable,
  FieldValue,
  FieldViewValue,
} from '@/components/layout/field-row'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Flex } from '@/components/ui/flex'
import { NumericInput } from '@/components/ui/numeric-input'
import { Radio } from '@/components/ui/radio'
import { SelectOption } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { formatPrice, transformDollarsToCents } from '@/utils/currency'

import {
  PAY_RENT_FEE_FIELDS,
  PAY_RENT_FEE_MAX_PERCENTAGE,
  PAY_RENT_FEE_TYPES,
  PAY_RENT_MAX_PROCESSING_DELAY,
  PayRentFeeFieldName,
  PayRentFormData,
} from './pay-rent.utils'

const PERCENTAGE_DECIMALS = 1

const SETTING_SWITCHES = ['allowAutopay', 'allowChargesSelection', 'allowPartialPayment'] as const

interface PayRentFeeFieldProps {
  type: ProcessingFeeType
}

// Note: typing into one input clears the other, so the filled input is always the active mode
const PayRentFeeField = ({ type }: PayRentFeeFieldProps) => {
  const { t } = useTranslation(['organisation'])
  const { setValue, trigger } = useFormContext<PayRentFormData>()
  const fields = PAY_RENT_FEE_FIELDS[type]
  const percentage = useController<PayRentFormData, PayRentFeeFieldName>({
    name: fields.percentage,
  })
  const flatAmount = useController<PayRentFormData, PayRentFeeFieldName>({
    name: fields.flatAmount,
  })

  const handleChange = (
    onChange: (value: string) => void,
    counterpart: PayRentFeeFieldName,
    value: string,
  ) => {
    onChange(value)
    if (value) setValue(counterpart, '', { shouldDirty: true })
    // Note: the "percentage or flat amount" error sits on the percentage input, filling either clears it
    if (percentage.fieldState.error) trigger(fields.percentage)
  }

  return (
    <Flex align="flex-start" gap={12}>
      <Flex direction="column" flex={1}>
        <NumericInput
          aria-label={t(
            `organisation:places.integrations.payRentConfig.convenienceFee.${type}Percentage`,
          )}
          decimalPlaces={PERCENTAGE_DECIMALS}
          error={percentage.fieldState.error?.message}
          icon={PercentIcon}
          max={PAY_RENT_FEE_MAX_PERCENTAGE}
          mode="decimal"
          name={percentage.field.name}
          placeholder={t(
            'organisation:places.integrations.payRentConfig.convenienceFee.percentagePlaceholder',
          )}
          size="small"
          value={percentage.field.value}
          onBlur={percentage.field.onBlur}
          onChange={(value) => handleChange(percentage.field.onChange, fields.flatAmount, value)}
        />
      </Flex>
      <FieldValue>
        <Typography as="span" color="black500" size="text-medium">
          {t('organisation:places.integrations.payRentConfig.convenienceFee.or')}
        </Typography>
      </FieldValue>
      <Flex direction="column" flex={1}>
        <CurrencyInput
          aria-label={t(
            `organisation:places.integrations.payRentConfig.convenienceFee.${type}FlatAmount`,
          )}
          name={flatAmount.field.name}
          placeholder={t(
            'organisation:places.integrations.payRentConfig.convenienceFee.flatAmountPlaceholder',
          )}
          size="small"
          value={flatAmount.field.value}
          onBlur={flatAmount.field.onBlur}
          onChange={(value) => handleChange(flatAmount.field.onChange, fields.percentage, value)}
        />
      </Flex>
    </Flex>
  )
}

const PayRentBatchField = ({ isRentManager }: { isRentManager: boolean }) => {
  const { t } = useTranslation(['organisation'])
  const { setValue } = useFormContext<PayRentFormData>()
  const dailyBatch = useWatch<PayRentFormData, 'dailyBatch'>({ name: 'dailyBatch' })

  const select = (value: boolean) =>
    setValue('dailyBatch', value, { shouldDirty: true, shouldTouch: true })

  return (
    <FieldValue>
      <Flex align="center" gap={24}>
        <Radio
          checked={!dailyBatch}
          label={t('organisation:places.integrations.payRentConfig.dailyBatch.individual')}
          name="dailyBatch"
          onChange={() => select(false)}
        />
        {!isRentManager && (
          <Radio
            checked={dailyBatch}
            label={t('organisation:places.integrations.payRentConfig.dailyBatch.batch')}
            name="dailyBatch"
            onChange={() => select(true)}
          />
        )}
      </Flex>
    </FieldValue>
  )
}

export const formatPayRentFee = (percentage: string, flatAmount: string) =>
  flatAmount
    ? formatPrice(transformDollarsToCents(flatAmount), { zeroPrice: true })
    : `${percentage || 0}%`

interface PayRentFieldsProps {
  isEditing: boolean
  isRentManager: boolean
  values: PayRentFormData
}

export const PayRentFields = ({ isEditing, isRentManager, values }: PayRentFieldsProps) => {
  const { t } = useTranslation(['organisation'])

  const codeOptions: SelectOption<string>[] = values.chargeCodesMappings.map(({ code }) => ({
    label: code,
    value: code,
  }))

  const renderRow = (key: string, label: string, view: ReactNode, control: ReactNode) => (
    <FieldRow key={key} label={label}>
      {isEditing ? control : <FieldViewValue>{view}</FieldViewValue>}
    </FieldRow>
  )

  const renderCodeSelect = (
    name: 'amenityChargeCode' | 'prePaidChargeCode',
    excluded: string | null,
  ) =>
    renderRow(
      name,
      t(`organisation:places.integrations.payRentConfig.${name}.label`),
      values[name],
      <SelectFormField<PayRentFormData, string>
        name={name}
        options={codeOptions.filter(({ value }) => value !== excluded)}
        placeholder={t(`organisation:places.integrations.payRentConfig.${name}.placeholder`)}
        size="small"
        type="single"
      />,
    )

  return (
    <FieldsTable>
      <FieldGroup>
        <FieldGroupHeader>
          {t('organisation:places.integrations.payRentConfig.convenienceFee.title')}
        </FieldGroupHeader>
        {PAY_RENT_FEE_TYPES.map((type) => {
          const fields = PAY_RENT_FEE_FIELDS[type]
          return renderRow(
            type,
            t(`organisation:places.integrations.payRentConfig.convenienceFee.${type}`),
            formatPayRentFee(values[fields.percentage], values[fields.flatAmount]),
            <PayRentFeeField type={type} />,
          )
        })}
      </FieldGroup>

      <FieldGroup>
        <FieldGroupHeader>
          {t('organisation:places.integrations.payRentConfig.groups.processing')}
        </FieldGroupHeader>
        {!isRentManager && renderCodeSelect('prePaidChargeCode', values.amenityChargeCode)}
        {!isRentManager && renderCodeSelect('amenityChargeCode', values.prePaidChargeCode)}
        {renderRow(
          'dailyBatch',
          t('organisation:places.integrations.payRentConfig.dailyBatch.label'),
          values.dailyBatch
            ? t('organisation:places.integrations.payRentConfig.dailyBatch.batch')
            : t('organisation:places.integrations.payRentConfig.dailyBatch.individual'),
          <PayRentBatchField isRentManager={isRentManager} />,
        )}
        {renderRow(
          'processingDelay',
          t('organisation:places.integrations.payRentConfig.processingDelay.label'),
          values.processingDelay,
          <NumericInputFormField
            autoComplete="off"
            max={PAY_RENT_MAX_PROCESSING_DELAY}
            min={0}
            mode="integer"
            name="processingDelay"
            placeholder={t(
              'organisation:places.integrations.payRentConfig.processingDelay.placeholder',
            )}
            size="small"
          />,
        )}
      </FieldGroup>

      <FieldGroup>
        <FieldGroupHeader>
          {t('organisation:places.integrations.payRentConfig.groups.residents')}
        </FieldGroupHeader>
        {SETTING_SWITCHES.map((name) =>
          renderRow(
            name,
            t(`organisation:places.integrations.payRentConfig.${name}`),
            <FieldBooleanBadge value={values[name]} />,
            <FieldValue>
              <SwitchFormField name={name} size="small" />
            </FieldValue>,
          ),
        )}
      </FieldGroup>

      {values.chargeCodesMappings.length > 0 && (
        <FieldGroup>
          <FieldGroupHeader>
            {t('organisation:places.integrations.payRentConfig.chargeCodesMappings.title')}
          </FieldGroupHeader>
          {values.chargeCodesMappings.map(({ code, description }, index) =>
            renderRow(
              code,
              code,
              description,
              <TextInputFormField
                autoComplete="off"
                name={`chargeCodesMappings.${index}.description`}
                placeholder={t(
                  'organisation:places.integrations.payRentConfig.chargeCodesMappings.placeholder',
                )}
                size="small"
              />,
            ),
          )}
        </FieldGroup>
      )}
    </FieldsTable>
  )
}
